// lib/anonAdoption.ts — Moving Out arc, drop 9 prerequisite
// ─────────────────────────────────────────────────────────────────────────
// WHAT HAPPENS TO THE PLAN SOMEONE BUILT BEFORE SIGNING UP.
//
// Today: nothing. `hasAnonData`, `adoptAnonData` and `discardAnonData` have
// existed in `lib/sessionMode` since before this arc and have NO IMPORTER
// anywhere. `scenariosSlice` reads `activeScenariosKey()`, which flips the
// instant auth resolves, so a person who builds a plan anonymously and signs
// up watches it disappear. The plan that motivated the signup is stranded in
// `nisk_scenarios_anon` and nothing ever reads it again.
//
// That is finding 18, and it is the fifth module in this arc found built,
// tested and wired to nothing.
//
// ─────────────────────────────────────────────────────────────────────────
// THE RULES, ruled 2026-08-01.
//
//   1. Signed-in account has NO plan data  -> adopt automatically.
//   2. Signed-in account HAS plan data     -> one-time import or discard,
//                                             with IMPORT as the primary.
//   3. Never delete anonymous data until the authenticated write SUCCEEDS.
//   4. A failed adoption leaves the anonymous plan recoverable.
//
// RULE 1 IS NOT A CONVENIENCE, IT IS THE WHOLE POINT. The overwhelmingly
// common case is a person with no account, building one plan, signing up
// because of it. Asking them to confirm they would like to keep the thing
// they just made is a dialog whose only possible useful answer is yes.
//
// RULE 2 IS A CONFLICT, NOT A PREFERENCE. Two sets of plans exist and the
// product cannot know which the person means. Import is primary because the
// anonymous work is the newer intent and the one at risk: signed-in plans
// are already safe, and a wrong "discard" is unrecoverable while a wrong
// "import" leaves two plans in a list.
//
// RULES 3 AND 4 ARE ONE RULE SEEN FROM BOTH SIDES, and they are why this
// module exists as a state machine rather than a boolean. "Adopt" is not an
// event, it is a sequence: read, write, verify, and only then delete. A
// `void` adopt cannot express that, because the caller cannot tell a
// successful write from a silent failure, and the delete happens either way.
//
// Pure and import-free (seams section 7). The storage calls belong to the
// caller; this decides WHAT should happen and in what order.
//
// Copy rule: no em or en dashes in any user-facing string.
// ─────────────────────────────────────────────────────────────────────────

/** What the two namespaces hold at the moment auth resolves. */
export interface AdoptionFacts {
	/** The anonymous namespace holds at least one plan. */
	anonHasPlans: boolean;
	/** The signed-in namespace holds at least one plan. */
	signedInHasPlans: boolean;
	/** True once this account has already answered the conflict question.
	 *  ONE TIME means one time: asking again on every sign-in turns a
	 *  decision into a nag, and the second answer is always the same as the
	 *  first because nothing has changed. */
	conflictAlreadyResolved: boolean;
}

export type AdoptionAction =
	/** Nothing anonymous to adopt. */
	| { kind: 'nothing-to-do' }
	/** Rule 1. No conflict, so no question. */
	| { kind: 'adopt-silently' }
	/** Rule 2. Two sets of plans; ask once. */
	| { kind: 'ask' }
	/** Asked and answered previously. The anonymous data stays where it is
	 *  rather than being deleted: an unanswered-again question is not
	 *  permission to throw work away. */
	| { kind: 'leave-alone' };

/**
 * What to do the moment auth resolves.
 *
 * ORDER MATTERS AND IT IS NOT THE OBVIOUS ONE. `nothing-to-do` is tested
 * first, so a person with no anonymous work never reaches the conflict
 * branch. Then `conflictAlreadyResolved`, so a second sign-in on the same
 * device does not re-ask. Only then the actual conflict.
 */
export function adoptionAction(facts: AdoptionFacts): AdoptionAction {
	if (!facts.anonHasPlans) return { kind: 'nothing-to-do' };
	if (facts.conflictAlreadyResolved) return { kind: 'leave-alone' };
	if (!facts.signedInHasPlans) return { kind: 'adopt-silently' };
	return { kind: 'ask' };
}

// ─── The sequence ─────────────────────────────────────────────────────────

export type AdoptionStep =
	/** Read the anonymous payload. */
	| 'read'
	/** Write it into the signed-in namespace. */
	| 'write'
	/** Read it back and confirm it is there. */
	| 'verify'
	/** Only now, remove the anonymous copy. */
	| 'clear';

/**
 * The adoption sequence, in order, as data.
 *
 * Stated as a list rather than as control flow so the ordering rule is
 * something a test can assert and a reviewer can see. The failure this
 * prevents is subtle and total: clear-before-verify loses the plan whenever
 * the write silently fails, and a storage write CAN silently fail. Private
 * browsing defines the localStorage API and throws on use; quota can be
 * exceeded mid-write, leaving a truncated value that parses as valid JSON
 * and contains nothing.
 *
 * VERIFY IS NOT PARANOIA. `browserStore()` already probes localStorage for
 * exactly this reason, and the working-copy recovery already treats a write
 * as fallible. This is the same posture applied to the one write where
 * failure is unrecoverable.
 */
export const ADOPTION_SEQUENCE: readonly AdoptionStep[] = [
	'read',
	'write',
	'verify',
	'clear',
];

export type AdoptionOutcome =
	| { kind: 'adopted'; plansMoved: number }
	/** Any step failed. The anonymous copy is UNTOUCHED and the person can
	 *  try again, which is rule 4. */
	| { kind: 'failed'; failedAt: AdoptionStep; anonPreserved: true };

export interface AdoptionAttempt {
	/** The step that failed, or null when every step succeeded. */
	failedAt: AdoptionStep | null;
	plansMoved: number;
}

/**
 * Turn an attempt into an outcome.
 *
 * `anonPreserved` is a LITERAL `true`, not a boolean. There is no code path
 * that may report a failure with the anonymous data already gone, and typing
 * it as `boolean` would permit one to be written.
 */
export function adoptionOutcome(attempt: AdoptionAttempt): AdoptionOutcome {
	return attempt.failedAt === null
		? { kind: 'adopted', plansMoved: attempt.plansMoved }
		: { kind: 'failed', failedAt: attempt.failedAt, anonPreserved: true };
}

/** True when the sequence has reached the point of no return. Before
 *  `clear`, a failure costs nothing. */
export function isRecoverable(failedAt: AdoptionStep): boolean {
	return failedAt !== 'clear';
}

// ─── Copy ─────────────────────────────────────────────────────────────────

export const CONFLICT_TITLE = 'You already have plans saved';

export const CONFLICT_BODY =
	'You built a plan before signing in, and this account already has plans of its own. Bringing it in keeps both.';

/** IMPORT IS THE PRIMARY. A wrong discard cannot be undone; a wrong import
 *  leaves an extra plan in a list the person can delete. */
export const CONFLICT_IMPORT = 'Bring my plan in';
export const CONFLICT_DISCARD = 'Leave it behind';

/** Shown with the discard action, because "leave it behind" sounds
 *  reversible and is not. */
export const DISCARD_WARNING = 'This cannot be undone.';

export function adoptedToast(plansMoved: number): string {
	return plansMoved === 1
		? 'Your plan is saved to your account.'
		: `Your ${plansMoved} plans are saved to your account.`;
}

